import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Wand2, Video, TrendingUp, Settings, User as UserIcon,
  LogOut, Sun, Moon, ShieldCheck, Cpu, CreditCard, Radar, Image as ImageIcon, Menu,
} from "lucide-react";
import { useAuth } from "../lib/auth-context";
import { useTheme } from "../lib/theme-context";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import OnboardingModal from "./OnboardingModal";

const items = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/generator", label: "Script Studio", icon: Wand2 },
  { to: "/dashboard/thumbnails", label: "Thumbnail Studio", icon: ImageIcon },
  { to: "/dashboard/images", label: "Image Studio", icon: Sparkles, badge: "NEW" },
  { to: "/dashboard/trends", label: "Trend Radar", icon: Radar },
  { to: "/dashboard/videos", label: "Video Ideas", icon: Video },
  { to: "/dashboard/analytics", label: "Analytics", icon: TrendingUp },
  { to: "/dashboard/ai-providers", label: "AI Providers", icon: Cpu },
  { to: "/dashboard/billing", label: "Billing", icon: CreditCard },
  { to: "/dashboard/profile", label: "Profile", icon: UserIcon },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
];

export default function DashboardLayout({ admin = false }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const Sidebar = ({ onNavigate }) => (
    <>
      <div className="h-16 px-6 flex items-center border-b border-border">
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2 font-display font-semibold" data-testid="dash-brand">
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-pink-500">
            <Sparkles className="w-4 h-4 text-zinc-950" />
          </span>
          TikBoost <span className="text-cyan-400">AI</span>
        </Link>
      </div>
      <nav className="p-4 flex flex-col gap-1 text-sm">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNavigate}
            data-testid={`sidebar-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center justify-between gap-2 px-3 py-2 rounded-lg transition-colors ${
                isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`
            }
          >
            <span className="flex items-center gap-3">
              <it.icon className="w-4 h-4" />
              {it.label}
            </span>
            {it.badge && <span className="text-[10px] uppercase tracking-widest text-cyan-400 border border-cyan-500/40 rounded-full px-2 py-0.5">{it.badge}</span>}
          </NavLink>
        ))}
        {admin && (
          <>
            <div className="mt-4 mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-3">Admin</div>
            <NavLink to="/admin" end onClick={onNavigate} data-testid="sidebar-admin-home" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}>
              <ShieldCheck className="w-4 h-4" /> Command Center
            </NavLink>
            <NavLink to="/admin/users" onClick={onNavigate} data-testid="sidebar-admin-users" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}>
              <UserIcon className="w-4 h-4" /> Users
            </NavLink>
            <NavLink to="/admin/analytics" onClick={onNavigate} data-testid="sidebar-admin-analytics" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}>
              <TrendingUp className="w-4 h-4" /> Analytics
            </NavLink>
          </>
        )}
      </nav>
      <div className="mt-auto p-4 border-t border-border">
        <Link
          to="/dashboard/billing"
          onClick={onNavigate}
          className="block rounded-xl border border-cyan-500/25 bg-cyan-500/5 p-3 hover:border-cyan-500/50 transition-colors"
          data-testid="sidebar-plan-card"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest text-cyan-300">Plan</p>
            <span className="text-[10px] text-muted-foreground group-hover:text-foreground">Manage</span>
          </div>
          <p className="mt-1 text-sm font-medium capitalize">{user?.subscription?.plan || "free"}</p>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Credits</p>
            <p className="text-xs font-mono">{user?.usage?.credits ?? 0}</p>
          </div>
        </Link>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-shell">
      <OnboardingModal />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card/40 backdrop-blur-xl sticky top-0 h-screen overflow-y-auto">
        <Sidebar />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border flex items-center justify-between px-4 lg:px-8 backdrop-blur-xl bg-background/70 sticky top-0 z-40">
          <div className="lg:hidden flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open navigation menu" data-testid="mobile-sidebar-toggle">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-card border-r border-border overflow-y-auto flex flex-col">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <Sidebar onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <Link to="/" className="flex items-center gap-2 font-display font-semibold">
              <span className="grid place-items-center w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400 to-pink-500">
                <Sparkles className="w-3.5 h-3.5 text-zinc-950" />
              </span>
              TikBoost
            </Link>
          </div>
          <div className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground">
            <span>Welcome back,</span>
            <span className="text-foreground font-medium">{user?.name || "Creator"}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="dash-theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="dash-user-menu" className="flex items-center gap-2 rounded-full p-1 hover:bg-secondary transition-colors">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-cyan-500 text-zinc-950">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} data-testid="menu-profile">
                  <UserIcon className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                {admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Admin Console
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
